import type { MixedItemTag } from "@memorang/types";
import { useMemo } from "react";
import {
	TAG_TYPES,
	computeStructuredTagCounts,
	getUnavailableQuestionTypeIds,
} from "../helpers/session-filters";
import type { CheckboxState } from "../types/filter";

export const useStructuredTagFilters = ({
	topLevelTags,
	selectedTagCheckboxMap,
	selectedDistributionCheckboxMap,
}: {
	topLevelTags: MixedItemTag[];
	selectedTagCheckboxMap: CheckboxState;
	selectedDistributionCheckboxMap: CheckboxState;
}) => {
	const hasSkillTags = useMemo(
		() => topLevelTags.some((t) => t.type === TAG_TYPES.skill),
		[topLevelTags],
	);

	const selectedSkillTag = useMemo(
		() =>
			topLevelTags.find(
				(t) => t.type === TAG_TYPES.skill && selectedTagCheckboxMap?.[t.id],
			),
		[topLevelTags, selectedTagCheckboxMap],
	);

	const unavailableQuestionTypeIds = useMemo<Set<number>>(() => {
		if (!hasSkillTags) {
			return new Set();
		}
		return getUnavailableQuestionTypeIds(topLevelTags, selectedSkillTag);
	}, [hasSkillTags, topLevelTags, selectedSkillTag]);

	const structuredTagCounts = useMemo(() => {
		if (!hasSkillTags) {
			return {};
		}
		return computeStructuredTagCounts(
			topLevelTags,
			selectedTagCheckboxMap,
			selectedDistributionCheckboxMap,
		);
	}, [
		hasSkillTags,
		topLevelTags,
		selectedTagCheckboxMap,
		selectedDistributionCheckboxMap,
	]);

	return {
		hasSkillTags,
		unavailableQuestionTypeIds,
		structuredTagCounts,
	};
};
